import { useMemo, useRef, useState, type ComponentProps, type ComponentType } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Check, ChevronDown, Search } from 'lucide-react'
import type { PropertyOption, usePropertyOptions } from '@/hooks/use-property-options'
import { propertySwitchPath } from '@/lib/property-routes'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { cn } from '@/lib/utils'

/**
 * Radix's public `DropdownMenuContentProps` omits `onOpenAutoFocus`, which the
 * primitive still honors at runtime (same alias as reassign-message-dropdown):
 * the picker opens with focus in its search box rather than on the menu.
 */
const DropdownMenuContentWithAutoFocus = DropdownMenuContent as ComponentType<
  ComponentProps<typeof DropdownMenuContent> & { onOpenAutoFocus?: (event: Event) => void }
>

function HighlightedName({ name, query }: { name: string; query: string }) {
  const lowered = name.toLowerCase()
  // Lowercasing can change a string's length (e.g. "İ"), which would shift
  // the slice off the match; such a name is shown without the underline.
  const index = query && lowered.length === name.length ? lowered.indexOf(query.toLowerCase()) : -1
  if (index < 0) return <>{name}</>
  return (
    <>
      {name.slice(0, index)}
      <mark className="bg-transparent text-inherit underline decoration-mint underline-offset-2">
        {name.slice(index, index + query.length)}
      </mark>
      {name.slice(index + query.length)}
    </>
  )
}

interface PropertyPickerProps {
  propertyId: string
  /** The current property's name, or null while it is not known yet. */
  name: string | null
  /** The property list, from the header's `usePropertyOptions()`. */
  list: ReturnType<typeof usePropertyOptions>
}

/**
 * The property name as a dropdown trigger (CAP-1034): type to filter, arrow
 * keys to move, Enter to switch, Escape to close and return to the name.
 */
export function PropertyPicker({ propertyId, name, list }: PropertyPickerProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [inputFocused, setInputFocused] = useState(false)
  const { options, isLoading, error, refetch } = list

  const trimmed = query.trim()
  const filtered = useMemo(() => {
    const needle = trimmed.toLowerCase()
    return needle ? options.filter(option => option.name.toLowerCase().includes(needle)) : options
  }, [options, trimmed])
  const active = filtered.filter(option => !option.archived)
  const archived = filtered.filter(option => option.archived)
  const firstMatch = trimmed ? (active[0] ?? archived[0]) : undefined

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setQuery('')
  }

  const select = (id: string) => {
    handleOpenChange(false)
    if (id !== propertyId) navigate(propertySwitchPath(location.pathname, location.search, id))
  }

  const focusItem = (which: 'first' | 'last') => {
    const items = listRef.current?.querySelectorAll<HTMLElement>(
      '[role="menuitem"]:not([data-disabled])'
    )
    if (!items?.length) return
    items[which === 'first' ? 0 : items.length - 1].focus()
  }

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      focusItem(event.key === 'ArrowDown' ? 'first' : 'last')
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (firstMatch) select(firstMatch.id)
    } else if (event.key.length === 1) {
      // Keep the menu's own typeahead from moving focus off the search box.
      event.stopPropagation()
    }
  }

  // A printable key or Backspace pressed while an item has focus (a hover
  // focuses one) edits the filter and returns focus to the search box, so
  // typing works wherever focus is instead of jumping between items. The edit
  // is applied here rather than left to the browser, which would apply it to
  // the item the key went to.
  const handleContentKeyDownCapture = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target === inputRef.current) return
    if (event.ctrlKey || event.metaKey || event.altKey) return
    const isBackspace = event.key === 'Backspace'
    if (!isBackspace && (event.key.length !== 1 || event.key === ' ')) return
    event.preventDefault()
    event.stopPropagation()
    setQuery(current => (isBackspace ? current.slice(0, -1) : current + event.key))
    inputRef.current?.focus()
  }

  const renderItem = (option: PropertyOption) => {
    const isCurrent = option.id === propertyId
    return (
      <DropdownMenuItem
        key={option.id}
        onSelect={() => select(option.id)}
        aria-current={isCurrent ? 'page' : undefined}
        // Enter in the search box picks this one; shown only while the search box
        // has focus, so it never looks selected beside a focused item.
        data-first-match={option === firstMatch && inputFocused ? '' : undefined}
        className={cn(
          'cursor-pointer justify-between gap-2 rounded-input px-2.5 py-1.5 focus:bg-surface2 data-[first-match]:bg-surface2',
          option.archived ? 'text-textFaint' : 'text-text',
          isCurrent && 'font-semibold text-mint'
        )}
      >
        <span className="min-w-0 truncate">
          <HighlightedName name={option.name} query={trimmed} />
        </span>
        {isCurrent && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
      </DropdownMenuItem>
    )
  }

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={name ? `Switch property: ${name}` : 'Switch property'}
          className="group inline-flex min-w-0 max-w-full items-center gap-2 rounded-button border border-transparent px-2.5 py-1.5 text-lg font-semibold text-text transition-colors hover:border-border hover:bg-surface2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-bg data-[state=open]:border-border data-[state=open]:bg-surface2"
        >
          {name ? (
            <span className="min-w-0 truncate">{name}</span>
          ) : (
            <LoadingSkeleton className="h-5 w-48" data-testid="property-name-skeleton" />
          )}
          <ChevronDown
            className="h-4 w-4 shrink-0 text-textFaint transition-transform group-data-[state=open]:rotate-180"
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContentWithAutoFocus
        align="start"
        className="w-[300px] max-w-[calc(100vw-32px)] rounded-card border-borderStrong bg-surface p-2 shadow-lg"
        onOpenAutoFocus={event => {
          event.preventDefault()
          inputRef.current?.focus()
        }}
        onKeyDownCapture={handleContentKeyDownCapture}
      >
        <div className="relative mb-1.5">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-textFaint"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={handleInputKeyDown}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            placeholder="Search properties…"
            aria-label="Search properties"
            className="h-8 w-full rounded-input border border-borderStrong bg-surface2 pl-8 pr-2.5 text-sm text-text placeholder:text-textFaint focus-visible:border-mint focus-visible:outline-hidden"
          />
        </div>
        <div ref={listRef} className="max-h-80 overflow-y-auto">
          {isLoading && options.length === 0 ? (
            <DropdownMenuItem disabled className="text-textFaint">
              Loading properties…
            </DropdownMenuItem>
          ) : error && options.length === 0 ? (
            <DropdownMenuItem
              className="cursor-pointer text-textMuted focus:bg-surface2"
              onSelect={event => {
                event.preventDefault()
                void refetch()
              }}
            >
              Couldn&apos;t load properties. Try again
            </DropdownMenuItem>
          ) : filtered.length === 0 ? (
            <p className="px-2.5 py-2 text-sm text-textFaint">No properties match</p>
          ) : (
            <>
              {active.map(renderItem)}
              {archived.length > 0 && (
                <>
                  {active.length > 0 && <DropdownMenuSeparator className="bg-border" />}
                  <DropdownMenuLabel className="px-2.5 pb-1 pt-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-textFaint">
                    Archived
                  </DropdownMenuLabel>
                  {archived.map(renderItem)}
                </>
              )}
            </>
          )}
        </div>
      </DropdownMenuContentWithAutoFocus>
    </DropdownMenu>
  )
}
